import { format } from 'date-fns';
import pluralize from 'pluralize';

import { LanguageTag } from '@zen/utils/languageSettings';
import type { Nullable, Optional } from '@zen/utils/typescript';

import type { DateFormat, DateFormatOptions, DateTimeLocaleAndOptions } from '../types';
import { isParseableAsDate, parseDate } from './parsing';

const dateFormats: DateFormatOptions = {
  D_MMMM_YYYY: {
    locale: LanguageTag.BritishEnglish,
    options: { day: 'numeric', month: 'long', year: 'numeric' }
  },
  DDD: {
    locale: LanguageTag.BritishEnglish,
    options: { weekday: 'short' }
  },
  DDDD: {
    locale: LanguageTag.BritishEnglish,
    options: { weekday: 'long' }
  },
  MMMM_YYYY: {
    locale: LanguageTag.BritishEnglish,
    options: { month: 'long', year: 'numeric' }
  },
  MMM_D: {
    locale: LanguageTag.AmericanEnglish,
    options: { day: 'numeric', month: 'short' }
  },
  MMM_D_YYYY: {
    locale: LanguageTag.AmericanEnglish,
    options: { day: 'numeric', month: 'short', year: 'numeric' }
  }
};

const parseAndFormatDate = (inputString: string, formatOptions?: DateTimeLocaleAndOptions): Nullable<string> => {
  if (!isParseableAsDate(inputString)) return null;

  const parsed = parseDate(inputString);

  if (!formatOptions) return format(parsed, 'yyyy-MM-dd');

  return parsed.toLocaleDateString(formatOptions.locale, formatOptions.options);
};

export const formatDate = (inputString: string, dateFormatString: DateFormat = 'MMM_D_YYYY'): string =>
  parseAndFormatDate(inputString, dateFormats[dateFormatString]) || '-';

export const formatDateRange = (
  startDate: string,
  endDate: Optional<string> = undefined,
  dateFormatString: DateFormat = 'MMM_D_YYYY'
): Nullable<string> => {
  const dateFormat = dateFormats[dateFormatString];
  const formattedStartDate = parseAndFormatDate(startDate, dateFormat);

  if (!endDate) return formattedStartDate;

  const formattedEndDate = parseAndFormatDate(endDate, dateFormat);

  if (!formattedEndDate || formattedStartDate === formattedEndDate) return formattedStartDate;

  return `${formattedStartDate} - ${formattedEndDate}`;
};

export const formatDateAsISO8601 = (inputString: string): Nullable<string> => parseAndFormatDate(inputString);

export const toDateString = (date: Date | number): string => format(new Date(date), 'yyyy-MM-dd');

export const formatWeekdayName = (date: Date): string => format(date, 'EEEEE');

export const formatDateWithShortWeekday = (date: Date): string => {
  const weekday = date.toLocaleDateString(LanguageTag.BritishEnglish, { weekday: 'short' });
  const day = date.getDate();
  const month = date.toLocaleDateString(LanguageTag.BritishEnglish, { month: 'short' });
  const year = String(date.getFullYear()).slice(-2);

  return `${weekday} ${day} ${month} '${year}`;
};

export const getDateDifference = (delayInDays: number): string => {
  const count: number = Math.abs(delayInDays);
  const sign: string = delayInDays > 0 ? '+' : '-';

  if (!delayInDays) return '';

  return `${sign}${count} ${pluralize('day', count)}`;
};
